import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatExpenseCategory } from '../utils/expenseCategories';

/**
 * ExpenseReceiptPreview - A preview component for expense receipt customization
 * Used in BusinessSettingsPage to show how the expense receipt will look
 */
const ExpenseReceiptPreview = ({ expense, options = {} }) => {
    const { t } = useTranslation();
    // Helper to determine text color (black or white) based on background brightness
    const getContrastingColor = (hexColor) => {
        if (!hexColor) return '#000000';
        const hex = hexColor.replace('#', '');
        if (hex.length !== 6) return '#000000';

        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Luminance formula (Rec. 601)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        return luminance > 0.5 ? '#000000' : '#FFFFFF';
    };

    const textColor = getContrastingColor(expense?.invoice_color || '#ffffff');
    const mutedColor = textColor === '#FFFFFF' ? '#e0e0e0' : '#666666';

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(amount);
    };

    // Default values for preview
    const supplierName = expense.supplier_name || 'Proveedor Ejemplo S.A.S';
    const supplierTaxId = expense.supplier_tax_id || '900.123.456-7';
    const supplierPhone = expense.supplier_phone || '+57 301 234 5678';

    return (
        <div
            className="receipt-container"
            style={{
                backgroundColor: expense.invoice_color || '#fff',
                color: textColor,
                maxWidth: '350px',
                margin: '0 auto',
                padding: '20px',
                borderRadius: '8px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                fontSize: '0.85rem'
            }}
        >
            {/* Header with logo and business info */}
            <div className="text-center mb-3">
                {expense.logo_url && (
                    <img
                        src={expense.logo_url}
                        alt="Logo"
                        style={{ maxWidth: '100px', maxHeight: '80px', objectFit: 'contain', marginBottom: '8px' }}
                    />
                )}
                <h6 className="fw-bold mb-0">{expense.business_name || 'Nombre del Negocio'}</h6>
                {expense.business_address && <div className="small" style={{ color: textColor }}>{expense.business_address}</div>}
                {expense.business_phone && <div className="small" style={{ color: textColor }}>Tel: {expense.business_phone}</div>}
                {expense.fiscal_id && (
                    <div style={{ fontSize: '0.75rem', color: mutedColor }}>
                        {expense.fiscal_id_type ? `${expense.fiscal_id_type}: ` : ''}{expense.fiscal_id}
                    </div>
                )}
            </div>

            {/* Title */}
            <div className="border-top border-bottom py-2 mb-3" style={{ borderColor: mutedColor + '!important' }}>
                <h6 className="text-center fw-bold mb-0" style={{ fontSize: '0.9rem' }}>COMPROBANTE DE EGRESO</h6>
                <div className="text-center small" style={{ color: mutedColor }}>#{expense.expense_id || '001'}</div>
            </div>

            {/* Date and Supplier info */}
            <div className="mb-3" style={{ fontSize: '0.8rem' }}>
                <Row className="mb-1">
                    <Col xs={4} className="fw-bold" style={{ color: textColor }}>Fecha:</Col>
                    <Col xs={8} className="text-end" style={{ color: textColor }}>
                        {expense.date ? new Date(expense.date).toLocaleDateString('es-CO') : new Date().toLocaleDateString('es-CO')}
                    </Col>
                </Row>
                {options.show_supplier_info !== false && (
                    <>
                        <Row className="mb-1">
                            <Col xs={4} className="fw-bold" style={{ color: textColor }}>Proveedor:</Col>
                            <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierName}</Col>
                        </Row>
                        {supplierTaxId && (
                            <Row className="mb-1">
                                <Col xs={4} className="fw-bold" style={{ color: textColor }}>NIT/CC:</Col>
                                <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierTaxId}</Col>
                            </Row>
                        )}
                        {supplierPhone && (
                            <Row className="mb-1">
                                <Col xs={4} className="fw-bold" style={{ color: textColor }}>Teléfono:</Col>
                                <Col xs={8} className="text-end" style={{ color: textColor }}>{supplierPhone}</Col>
                            </Row>
                        )}
                    </>
                )}
            </div>

            {/* Expense Details */}
            {options.show_expense_details !== false && (
                <div className="mb-3">
                    <h6 className="fw-bold border-bottom pb-1 mb-2" style={{ color: textColor, borderColor: mutedColor, fontSize: '0.85rem' }}>
                        Detalles del Gasto
                    </h6>
                    <Row className="mb-1">
                        <Col xs={12} className="fw-bold mb-1" style={{ color: textColor }}>{formatExpenseCategory(expense.category, t) || t('expensesPage.categories.Other', 'Otros')}</Col>
                        <Col xs={12} className="small fst-italic mb-1" style={{ color: mutedColor }}>
                            {expense.description || 'Descripción del gasto'}
                        </Col>
                    </Row>
                    <Row className="mt-2 text-end">
                        <Col xs={12}>
                            <h5 className="fw-bold mb-0" style={{ color: textColor }}>{formatCurrency(expense.amount || 120000)}</h5>
                        </Col>
                    </Row>
                </div>
            )}

            {/* Payment Method */}
            {options.show_payment_method !== false && (
                <div className="border-top pt-2 mb-3" style={{ borderColor: mutedColor, fontSize: '0.8rem' }}>
                    <Row className="mb-1">
                        <Col xs={5} className="fw-bold" style={{ color: textColor }}>Método de Pago:</Col>
                        <Col xs={7} className="text-end" style={{ color: textColor }}>{expense.payment_method || 'Efectivo'}</Col>
                    </Row>
                </div>
            )}

            {/* Footer */}
            <div className="text-center mt-3 pt-2 border-top" style={{ borderColor: mutedColor }}>
                {/* QR Code */}
                {options.show_qr_code !== false && expense.qr_code_url && (
                    <div className="mb-2">
                        <img
                            src={expense.qr_code_url}
                            alt="QR Code"
                            style={{ width: '70px', height: '70px', objectFit: 'contain' }}
                        />
                    </div>
                )}

                <div className="small fst-italic" style={{ color: mutedColor }}>
                    {expense.invoice_footer || '¡Gracias por su servicio!'}
                </div>
                <div className="mt-1">
                    <small style={{ fontSize: '0.65rem', color: mutedColor }}>Powered by Evazard</small>
                </div>
            </div>
        </div>
    );
};

export default ExpenseReceiptPreview;
